let updateCallback
let waitingWorker
let reloading = false
let controllerChangeListening = false

/**
 * Предельное время ожидания установки нового service worker
 * при применении обновления по действию пользователя.
 *
 * @type {number}
 */
const installedWorkerTimeout = 10 * 1000

/**
 * Регистрация service worker снимается, если страница им управляется.
 * Ошибки подавляются: снятие регистрации выполняется как восстановление
 * после сбоя и не должно прерывать работу страницы.
 *
 * @returns {Promise<void>}
 */
const unregisterSW = async () => {
  try {
    if (navigator?.serviceWorker?.controller) {
      const registration = await navigator.serviceWorker.ready
      await registration.unregister()
    }
  } catch (error) {}
}

/**
 * Обработчик готовности обновления сохраняется. Если обновление найдено
 * раньше установки обработчика, обработчик вызывается сразу.
 *
 * @param {Function} callback обработчик готовности обновления
 * @returns {void}
 */
export const onUpdateSW = callback => {
  if (callback && typeof callback === 'function') {
    updateCallback = callback

    if (waitingWorker) {
      updateCallback()
    }
  }
}

/**
 * Возвращается текущая регистрация service worker. При отсутствии
 * поддержки или ошибке доступа возвращается undefined.
 *
 * @returns {Promise<ServiceWorkerRegistration|undefined>}
 */
const getRegistration = async () => {
  try {
    const registration = await navigator?.serviceWorker?.getRegistration?.()

    return registration
  } catch (error) {}
}

/**
 * Возвращает состояние регистрации service worker: поддержка браузером,
 * наличие регистрации, её область, адрес файла, состояние активного
 * обработчика и наличие обработчиков в состояниях waiting и installing.
 *
 * @returns {Promise<{ supported: boolean, registered: boolean, scope: string|undefined, scriptURL: string|undefined, state: string|undefined, waiting: boolean, installing: boolean }>}
 */
export const getStateSW = async () => {
  const state = {
    supported: !!navigator?.serviceWorker,
    registered: false,
    scope: undefined,
    scriptURL: undefined,
    state: undefined,
    waiting: false,
    installing: false
  }

  if (!state.supported) {
    return state
  }

  const registration = await getRegistration()
  if (!registration) {
    return state
  }

  state.registered = true
  state.scope = registration.scope
  state.scriptURL = registration.active?.scriptURL
  state.state = registration.active?.state
  state.waiting = !!registration.waiting
  state.installing = !!registration.installing

  return state
}

/**
 * Ожидается переход устанавливаемого service worker в состояние installed.
 * Если переход не произошёл за время installedWorkerTimeout,
 * возвращается undefined.
 *
 * @param {ServiceWorker} worker устанавливаемый service worker
 * @returns {Promise<ServiceWorker|undefined>}
 */
const waitForInstalledWorker = worker => {
  return new Promise(resolve => {
    const timeout = setTimeout(() => {
      resolve()
    }, installedWorkerTimeout)

    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed') {
        clearTimeout(timeout)
        resolve(worker)
      }
    })
  })
}

/**
 * Выполняется поиск service worker, ожидающего активации, с запросом
 * проверки обновления. Применяется, когда обновление запускается
 * до событий регистрации, например по данным version.json.
 *
 * @returns {Promise<ServiceWorker|undefined>}
 */
const findWaitingWorker = async () => {
  const registration = await getRegistration()
  if (!registration) {
    return
  }

  try {
    await registration.update()
  } catch (error) {}

  if (registration.waiting) {
    return registration.waiting
  }

  if (registration.installing) {
    return waitForInstalledWorker(registration.installing)
  }
}

/**
 * Готовое обновление применяется: ожидающему service worker отправляется
 * сообщение SKIP_WAITING, после чего перезагрузка выполняется по событию
 * controllerchange. Если подходящего service worker нет, страница
 * перезагружается напрямую.
 *
 * @returns {Promise<void>}
 */
export const applyUpdateSW = async () => {
  const worker = waitingWorker || await findWaitingWorker()
  if (worker) {
    worker.postMessage({ type: 'SKIP_WAITING' })

    return
  }

  window.location.reload()
}

/**
 * Service worker, ожидающий активации, запоминается, обработчик
 * готовности обновления уведомляется.
 *
 * @param {ServiceWorker} worker service worker, ожидающий активации
 * @returns {void}
 */
const setWaitingWorker = worker => {
  waitingWorker = worker
  updateCallback?.()
}

/**
 * Устанавливается обработчик смены управляющего service worker.
 * Обработчик добавляется один раз за время жизни страницы,
 * перезагрузка выполняется не более одного раза.
 *
 * @returns {void}
 */
const addControllerChangeListener = () => {
  if (controllerChangeListening) {
    return
  }

  controllerChangeListening = true
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!reloading) {
      reloading = true
      window.location.reload()
    }
  })
}

/**
 * Выполняется подписка на события регистрации с уведомлением о готовом
 * обновлении. Обновление считается готовым, когда новый service worker
 * установлен, а страницей управляет предыдущий service worker.
 *
 * @param {ServiceWorkerRegistration} registration текущая регистрация
 * @returns {void}
 */
const addSWListeners = registration => {
  if (registration.waiting && navigator.serviceWorker.controller) {
    setWaitingWorker(registration.waiting)
  }

  registration.addEventListener('updatefound', () => {
    const worker = registration.installing
    if (worker) {
      worker.addEventListener('statechange', () => {
        if (worker.state === 'installed' && navigator.serviceWorker.controller) {
          setWaitingWorker(worker)
        }
      })
    }
  })
}

/**
 * Service worker регистрируется, выполняется подписка на события его
 * обновления. Если регистрация завершилась ошибкой, прежняя регистрация
 * снимается.
 *
 * @param {string} swUrl адрес файла service worker
 * @returns {Promise<void>}
 */
const register = async swUrl => {
  try {
    const registration = await navigator?.serviceWorker?.register?.(swUrl)
    if (registration) {
      registration.update()
      addSWListeners(registration)
    }
  } catch (error) {
    unregisterSW()
  }
}

/**
 * Ожидается полная загрузка страницы, чтобы регистрация service worker
 * не соперничала за сеть с ресурсами первой отрисовки.
 *
 * @returns {Promise<void>}
 */
const waitForPageLoad = () => {
  if (document.readyState === 'complete') {
    return Promise.resolve()
  }

  return new Promise(resolve => {
    window.addEventListener('load', resolve, { once: true })
  })
}

/**
 * Регистрация service worker запускается после загрузки страницы только
 * в сборке для рабочего окружения. Файл service worker запрашивается
 * в обход кеша, регистрация выполняется только при успешном ответе
 * с типом application/javascript: это защищает от подстановки страницы
 * вместо файла сборки.
 *
 * @returns {Promise<void>}
 */
export const registerSW = async () => {
  if (process.env.NODE_ENV === 'production') {
    if (navigator?.serviceWorker) {
      addControllerChangeListener()
      await waitForPageLoad()

      try {
        const swUrl = '/sw.js'
        const response = await fetch(swUrl, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        })
        if (response?.ok) {
          const contentType = response.headers.get('content-type')
          if (contentType?.includes('application/javascript')) {
            register(swUrl)
          }
        }
      } catch (error) {}
    }
  }
}
